import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import * as datefns from 'date-fns'
import styled from '@emotion/styled'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import LinearProgress from '@mui/material/LinearProgress'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import MuiAccordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'

import { LeavePageConfirmationDialog } from '../../../../components/dialogs/LeavePageConfirmationDialog'
import { DeleteConvertibleConfirmationDialog } from './AdminDeleteConvertibleConfirmationDialog'
import { ConvertibleEntity } from 'types/ConvertibleEntity'
import AccordionDetails from '@mui/material/AccordionDetails'
import AdvisorDetails from '../../issues/AdminAdvisorDetails'
import ConvertiblesApi from '../../../../api/ConvertiblesApi'
import TermsFields from './AdminConvertibleTermsFields'
import InvestorsFields, { InvestorFormWithId } from '../../shared/investors/AdminInvestorsFields'
import EventsFields from './AdminConvertibleEventsFields'
import { SourcesFields } from '../../shared/source-fields'
import LinkedIssuesDetails from '../../issues/linked-issues/AdminLinkedIssuesDetails'
import { InvestorsFieldsSanityCheckProps } from '../../shared/investors/AdminInvestorFieldsSanityCheck'

type AdminEditConvertibleFormComponent = React.FC<{
    convertible: ConvertibleEntity
    refetchData: (id: string) => Promise<void>
}>

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&::before': {
        display: 'none',
    },
}))

const FormAccordionSummary = ({ children }: { children: React.ReactNode }) => {
    return (
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='h6'>{children}</Typography>
        </AccordionSummary>
    )
}

const extractAdvisorFormData = (advisor) => {
    const { id, name, type } = advisor
    return { id, name, type }
}

const investorsValuesSanityChecker = (convertible: ConvertibleEntity) => (investors: InvestorFormWithId[]) => {
    const calcTotals = (investors: InvestorFormWithId[]) => {
        const toF = (str: string | number | null) => (str ? parseFloat(str as string) : 0)
        const cashValue = (p: InvestorFormWithId, convertible: ConvertibleEntity) =>
            p.amountInCash ? toF(p.amountInCash) : toF(p.amountInPercentage) * toF(convertible.loanAmount)
        const sums = investors.reduce(
            (acc, p) => {
                acc.cash += toF(p.amountInCash) || 0
                acc.percentage += toF(p.amountInPercentage) || 0
                acc.level0 += cashValue(p, convertible)
                return acc
            },
            { cash: 0, percentage: 0, level0: 0, level1: 0 },
        )
        return {
            cash: sums.cash + sums.percentage * 0.01 * convertible.loanAmount,
            percentage: sums.percentage + (sums.cash / convertible.loanAmount) * 100,
            level0: sums.level0,
            level1: sums.level1,
        }
    }
    const totals = calcTotals(investors)

    const calcWarnings = (totals: InvestorsFieldsSanityCheckProps['totals'], convertible: ConvertibleEntity) => {
        let warningOfferedUnits = false
        const totalOfferingCash = convertible.loanAmount

        if (totalOfferingCash !== null) {
            warningOfferedUnits = (totals.cash - totalOfferingCash) / totalOfferingCash > 0.01
        }

        return {
            warningOfferedUnits,
            totalOfferingCash,
        }
    }
    const warnings = calcWarnings(totals, convertible)

    return {
        totals,
        warnings,
    } as InvestorsFieldsSanityCheckProps
}

const AdminEditConvertibleForm: AdminEditConvertibleFormComponent = ({ convertible, refetchData }) => {
    const formMethods = useForm({
        defaultValues: convertible,
    })
    const [isFormDirty, setIsFormDirty] = useState(false)
    const [isCustomFormDirty, setIsCustomFormDirty] = useState(false)
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
    const [saving, setSaving] = useState(false)
    const [lastSaved, setLastSaved] = useState<Date | null>(null)
    const [noInvestorsNamedChecked, setNoInvestorsNamedChecked] = useState(!!formMethods.getValues('noInvestorsNamed'))

    const investorsValuesSanityCheckerMemo = React.useMemo(
        () => investorsValuesSanityChecker(convertible),
        [convertible],
    )

    const convertibleApi = new ConvertiblesApi()

    const navigate = useNavigate()

    useEffect(() => {
        formMethods.reset({ ...convertible })
    }, [convertible])

    const dirtyFormFields = Object.keys(formMethods.formState.dirtyFields)

    const sources = formMethods.getValues('sources') || []
    const publicationSource = sources.some((source) => source.sourceType === 'Publication')
    const conversionExists = !!formMethods.watch('vwap') || !!formMethods.watch('fixedConversionPrice')
    const loanAmount = formMethods.watch('loanAmount')
    const arrangementFee = formMethods.watch('arrangementFee')
    const annualInterest = formMethods.watch('annualInterest')
    const monthlyInterest = formMethods.watch('monthlyInterest')

    const disableSaving =
        !publicationSource ||
        !conversionExists ||
        loanAmount === null ||
        (arrangementFee !== null && parseFloat(arrangementFee) === 0) ||
        (annualInterest !== null && parseFloat(annualInterest) === 0) ||
        (monthlyInterest !== null && parseFloat(monthlyInterest) === 0)

    const mandatoryFieldsMissing = (() => {
        const missingFields: string[] = []
        if (loanAmount === null) missingFields.push('lånebelopp')
        if (!conversionExists) missingFields.push('konverteringspris')
        if (!publicationSource) missingFields.push('källa')
        return missingFields.length > 0 ? missingFields.join(', ') : null
    })()

    useEffect(() => {
        if (dirtyFormFields.length > 0) {
            setIsFormDirty(true)
        } else {
            setIsFormDirty(false)
        }
    }, [dirtyFormFields])

    const handleUpdateConvertible = async () => {
        if (saving) return
        if (disableSaving) return
        setSaving(true)
        const data = formMethods.getValues()

        data.advisors = data.advisors.map(extractAdvisorFormData)
        // replace all '' with null
        Object.keys(data).forEach((key) => {
            if (data[key] === '') data[key] = null
        })

        const removeAttributes = ['feeLevel', 'leo', 'isSeller']
        removeAttributes.forEach((attr) => {
            if (['feeLevel', 'leo', 'isSeller'].includes(attr)) {
                data.investors.forEach((investor) => delete investor[attr])
            } else {
                delete data[attr]
            }
        })

        const updatedConvertible = await convertibleApi.update(data)
        if (updatedConvertible) {
            setLastSaved(new Date())
            void refetchData(convertible.id)
            formMethods.reset()
        } else {
            console.log('error result: no updated warrant')
        }
        setIsCustomFormDirty(false)
        setSaving(false)
    }

    const handleDeleteConvertible = async () => {
        if (saving) return
        setSaving(true)
        const allDeleted = await convertibleApi.delete(convertible.id)
        console.log({ allDeleted })
        navigate('/admin/convertibles')
        setSaving(false)
    }

    return (
        <FormProvider {...formMethods}>
            <LeavePageConfirmationDialog isDirty={isFormDirty || isCustomFormDirty} />
            <DeleteConvertibleConfirmationDialog
                open={deleteConfirmationOpen}
                onClose={() => setDeleteConfirmationOpen(false)}
                onDelete={handleDeleteConvertible}
            />

            <Card sx={{ mb: 6 }}>
                <CardContent>
                    {!convertible && <LinearProgress />}
                    {convertible && (
                        <Grid container sx={{ mb: 2 }} spacing={1} direction='row'>
                            <Grid size={{ xs: 12, md: 6 }}>
                                {mandatoryFieldsMissing ? (
                                    <Typography variant='h5' color='error'>
                                        Lägg till följande datapunkter: {mandatoryFieldsMissing}
                                    </Typography>
                                ) : null}
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Typography variant='body1'>Aktie: {convertible.instrumentName}</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 1 }}>
                                <Button
                                    variant='outlined'
                                    color='warning'
                                    fullWidth
                                    onClick={() => setDeleteConfirmationOpen(true)}
                                >
                                    Radera
                                </Button>
                            </Grid>
                            <Grid size={{ xs: 12, md: 1 }}>
                                <Stack sx={{ textAlign: 'center' }}>
                                    <Button
                                        variant='contained'
                                        color='primary'
                                        fullWidth
                                        onClick={formMethods.handleSubmit(handleUpdateConvertible)}
                                        disabled={saving || disableSaving}
                                    >
                                        Spara {saving && <CircularProgress size={24} />}
                                    </Button>
                                    <Typography variant='caption' color='textSecondary'>
                                        {lastSaved && datefns.format(lastSaved, 'H:mm:ss')}
                                    </Typography>
                                </Stack>
                            </Grid>
                        </Grid>
                    )}
                    <Accordion>
                        <FormAccordionSummary>Rådgivare</FormAccordionSummary>
                        <AccordionDetails>
                            <AdvisorDetails
                                entity={convertible}
                                entityType='convertible'
                                setIsCustomFormDirty={setIsCustomFormDirty}
                            />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <FormAccordionSummary>Länkade emissioner</FormAccordionSummary>
                        <AccordionDetails>
                            <LinkedIssuesDetails
                                convertible={convertible}
                                setIsCustomFormDirty={setIsCustomFormDirty}
                            />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded={loanAmount === null || !conversionExists}>
                        <FormAccordionSummary>Villkor</FormAccordionSummary>
                        <AccordionDetails>
                            <TermsFields convertible={convertible} setIsCustomFormDirty={setIsCustomFormDirty} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <FormAccordionSummary>Investerare</FormAccordionSummary>
                        <AccordionDetails>
                            <InvestorsFields
                                entityType='convertible'
                                acceptedFeeLevels={[]}
                                currency={convertible.currency}
                                isFeeLevelEnabled={false}
                                isLeoEnabled={false}
                                isSellerEnabled={false}
                                sanityChecker={investorsValuesSanityCheckerMemo}
                                noInvestorsNamedChecked={noInvestorsNamedChecked}
                                setNoInvestorsNamedChecked={setNoInvestorsNamedChecked}
                                setIsCustomFormDirty={setIsCustomFormDirty}
                                validateAmount={() => true}
                                validateLeo={() => true}
                            />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <FormAccordionSummary>Händelser</FormAccordionSummary>
                        <AccordionDetails>
                            <EventsFields convertible={convertible} setIsCustomFormDirty={setIsCustomFormDirty} />
                        </AccordionDetails>
                    </Accordion>
                    <Accordion defaultExpanded={!publicationSource}>
                        <FormAccordionSummary>Källor</FormAccordionSummary>
                        <AccordionDetails>
                            <SourcesFields
                                convertible={convertible}
                                allowUpload={false}
                                setIsCustomFormDirty={setIsCustomFormDirty}
                            />
                        </AccordionDetails>
                    </Accordion>
                </CardContent>
            </Card>
        </FormProvider>
    )
}

export default AdminEditConvertibleForm
